/**
 * Types for the statistics module
 */

export type TimePeriod = 'day' | 'week' | 'month' | 'year';
export type ChartType = 'line' | 'bar' | 'combo' | 'bubble';

/**
 * Week bucketing anchor for aggregated stats requests (timeScale = 'week').
 * Mirror of the API contract (StimulinAPI schemas/stats.ts — keep in sync):
 * - ISO_MONDAY: legacy behaviour, buckets snap to the Monday of each ISO week
 * - PERIOD_START: 7-day buckets anchored on the request startDate
 */
export const StatsWeekAnchor = {
    ISO_MONDAY: 'isoMonday',
    PERIOD_START: 'periodStart'
} as const;

export type StatsWeekAnchorValue = (typeof StatsWeekAnchor)[keyof typeof StatsWeekAnchor];

/**
 * Data for the connections chart
 */
export interface ConnectionStats {
    labels: string[];
    data: number[]; // minutes per period
    median?: number; // median across periods (minutes)
    totalMinutes?: number; // total minutes across the selected period
    evolution?: number;
}

/**
 * Data for the media import chart
 */
export interface MediaImportStats {
    labels: string[];
    data: number[];
    total?: number;
    evolution?: number;
}

/**
 * Data for the game creation chart
 */
export interface GameCreationStats {
    labels: string[];
    data: number[];
    total?: number;
    evolution?: number;
}

/**
 * Data for the media consultation chart
 */
export interface MediaConsultationStats {
    labels: string[];
    duration: number[];
    totalDuration?: number;
    total?: number; // alias for convenience (minutes)
    evolution?: number;
}

/**
 * Data for the game play chart
 */
export interface GamePlayStats {
    labels: string[];
    started: number[];
    finished: number[];
    totalStarted?: number;
    totalFinished?: number;
    evolution?: number;
}

/**
 * Data for the performance chart
 */
export interface PerformanceStats {
    labels: string[];
    difficulty: number[]; // median difficulty per period
    averageLevel?: number; // global average across the selected period (kept for backward compatibility)
    medianDifficulty?: number; // global median difficulty
    errors: number[];
    clues: number[];
    evolution?: number;
}

/**
 * Data for the game duration chart
 */
export interface GameDurationStats {
    labels: string[];
    averageLevel: number[]; // median level per period
    medianLevel?: number; // global median level
    medianDuration: number[]; // median duration per period (in minutes)
    medianDurationSeconds?: number[]; // median duration per period (in seconds)
    median?: number; // global median across the selected period (minutes)
    medianSeconds?: number; // global median across the selected period (seconds)
    totalDuration?: number; // total duration in seconds
    totalMinutes?: number; // total duration in minutes
    evolution?: number;
}

/**
 * Generic request for stats
 */
export interface StatsRequest {
    idList: string[];
    startDate: string;
    endDate: string;
    timeScale: TimePeriod;
    deletaTime?: number;
    type?: string;
    action?: string;
    actions?: string[];
    mediaType?: string;
    weekAnchor?: StatsWeekAnchorValue;
}

/**
 * Generic response from the stats API
 */
export interface StatsResponse<T = any> {
    status: number;
    message: string;
    data: T;
    evolution?: number;
}

/**
 * Preset of dates
 */
export interface DatePreset {
    label: string;
    startDate: Date;
    endDate: Date;
}

/**
 * Data for the game satisfaction per type chart
 */
export interface GameSatisfactionStats {
    labels: string[]; // values of game types (ex: quizz, puzzle)
    satisfactionRate: number[]; // average satisfaction rate (1-5) for each type
    totalVotes: number[]; // total number of votes for each type
    noOpinionVotes: number[]; // number of "no opinion" votes for each type
    totalVotesAll?: number; // total votes for selected period
    noOpinionVotesAll?: number; // total no opinion votes for selected period
    globalAverageRating?: number; // average rating across all votes with opinion (1-5)
    globalSatisfactionPercent?: number; // transformed into percentage 0-100 for display
    evolution?: number;
}

/**
 * Data for the Scoring Stimul'in chart
 */
export interface ScoringStats {
    labels: string[]; // Period labels
    scores: number[]; // Scores per period (0-100)
    gameCounts: number[]; // Number of games per period
    evolution?: number;
}
